import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
    LogOut, Users, Clock, CheckCircle, Loader2, 
    Settings, RefreshCw, ExternalLink, FileText, CreditCard, Copy, Key, Eye, EyeOff, Save, Pencil, X, Link2
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2c2cfe83-306e-4fb5-b370-367ad0387383/artifacts/k0uivc6c_4skr1hVuGATobkW3-light.png";

const ENTITY_TYPES = [
    'Individual', 'Sole Trader', 'Non-Registered Community Organisation',
    'Private Company Limited by Shares (LTD)', 'Designated Activity Company (DAC)',
    'Company Limited by Guarantee (CLG)', 'Owners Management Company (OMC)',
    'Public Limited Company (PLC)', 'Unlimited Company (ULC)', 'Societas Europaea Company (SE)'
];

const COUNTIES = [
    'Antrim', 'Armagh', 'Carlow', 'Cavan', 'Clare', 'Cork', 'Derry', 'Donegal',
    'Down', 'Dublin', 'Fermanagh', 'Galway', 'Kerry', 'Kildare', 'Kilkenny',
    'Laois', 'Leitrim', 'Limerick', 'Longford', 'Louth', 'Mayo', 'Meath',
    'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary', 'Tyrone',
    'Waterford', 'Westmeath', 'Wexford', 'Wicklow'
];

const GENDERS = ['Male', 'Female'];

const NATIONALITIES = [
    { code: 'IE', name: 'Irish' }, { code: 'GB', name: 'British' }, { code: 'US', name: 'American' },
    { code: 'DE', name: 'German' }, { code: 'FR', name: 'French' }, { code: 'ES', name: 'Spanish' },
    { code: 'IT', name: 'Italian' }, { code: 'PL', name: 'Polish' }, { code: 'PT', name: 'Portuguese' },
    { code: 'NL', name: 'Dutch' }, { code: 'OTHER', name: 'Other' }
];

export default function AdminDashboard() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [stats, setStats] = useState(null);
    const [applications, setApplications] = useState([]);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedApp, setSelectedApp] = useState(null);
    const [editedApp, setEditedApp] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    
    // API Key management state
    const [sandboxKey, setSandboxKey] = useState('');
    const [productionKey, setProductionKey] = useState('');
    const [showSandboxKey, setShowSandboxKey] = useState(false);
    const [showProductionKey, setShowProductionKey] = useState(false);
    const [savingKeys, setSavingKeys] = useState(false);
    
    // Redirect URL state
    const [redirectUrl, setRedirectUrl] = useState('');
    const [savingRedirectUrl, setSavingRedirectUrl] = useState(false);
    
    // Manual link account state
    const [manualRyftAccountId, setManualRyftAccountId] = useState('');

    const fetchData = useCallback(async () => {
        try {
            const [statsRes, appsRes, settingsRes] = await Promise.all([
                adminAPI.getStats(),
                adminAPI.getApplications({ limit: 50 }),
                adminAPI.getSettings()
            ]);
            setStats(statsRes.data);
            setApplications(appsRes.data.applications);
            setSettings(settingsRes.data);
            setSandboxKey(settingsRes.data.ryft_sandbox_key_set ? '••••••••••••••••' : '');
            setProductionKey(settingsRes.data.ryft_production_key_set ? '••••••••••••••••' : '');
            setRedirectUrl(settingsRes.data.ryft_redirect_url || '');
        } catch (err) {
            console.error('Failed to fetch data:', err);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleViewDetails = async (app) => {
        try {
            const response = await adminAPI.getApplication(app.id);
            setSelectedApp(response.data);
            setEditedApp(JSON.parse(JSON.stringify(response.data))); // Deep clone
            setIsEditing(false);
            setDetailDialogOpen(true);
        } catch (err) {
            toast.error('Failed to load application details');
        }
    };

    const handleStartEditing = () => {
        setEditedApp(JSON.parse(JSON.stringify(selectedApp)));
        setIsEditing(true);
    };

    const handleCancelEditing = () => {
        setEditedApp(JSON.parse(JSON.stringify(selectedApp)));
        setIsEditing(false);
    };

    const handleSaveChanges = async () => {
        setActionLoading(true);
        try {
            const response = await adminAPI.updateApplication(editedApp.id, editedApp);
            // Refresh the application data from server
            const updated = await adminAPI.getApplication(editedApp.id);
            setSelectedApp(updated.data);
            setEditedApp(JSON.parse(JSON.stringify(updated.data)));
            setIsEditing(false);
            
            if (response.data.warning) {
                toast.warning(response.data.warning);
            } else {
                toast.success('Application updated successfully');
            }
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save changes');
        } finally {
            setActionLoading(false);
        }
    };

    const updateEditedField = (section, field, value) => {
        setEditedApp(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const handleStatusChange = async (appId, newStatus) => {
        setActionLoading(true);
        try {
            await adminAPI.updateStatus(appId, newStatus);
            toast.success(`Application ${newStatus}`);
            fetchData();
            if (selectedApp?.id === appId) {
                setSelectedApp(prev => ({ ...prev, status: newStatus }));
                setEditedApp(prev => ({ ...prev, status: newStatus }));
            }
        } catch (err) {
            toast.error('Failed to update status');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCreateRyftAccount = async (appId) => {
        setActionLoading(true);
        try {
            const response = await adminAPI.createRyftAccount(appId);
            toast.success(response.data.mock ? 'RyftPay account created (MOCK MODE)' : 'RyftPay account created');
            fetchData();
            if (selectedApp?.id === appId) {
                const updated = await adminAPI.getApplication(appId);
                setSelectedApp(updated.data);
                setEditedApp(JSON.parse(JSON.stringify(updated.data)));
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create RyftPay account');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCreateRyftLink = async (appId) => {
        setActionLoading(true);
        try {
            const response = await adminAPI.createRyftLink(appId);
            if (response.data.onboarding_url) {
                navigator.clipboard.writeText(response.data.onboarding_url);
                toast.success(response.data.mock ? 'Onboarding link created and copied (MOCK MODE)' : 'Onboarding link created and copied to clipboard');
            }
            fetchData();
            if (selectedApp?.id === appId) {
                const updated = await adminAPI.getApplication(appId);
                setSelectedApp(updated.data);
                setEditedApp(JSON.parse(JSON.stringify(updated.data)));
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create onboarding link');
        } finally {
            setActionLoading(false);
        }
    };

    const handleLinkRyftAccount = async (appId) => {
        if (!manualRyftAccountId.trim()) {
            toast.error('Please enter a RyftPay Account ID');
            return;
        }
        setActionLoading(true);
        try {
            await adminAPI.linkRyftAccount(appId, manualRyftAccountId.trim());
            toast.success('RyftPay account linked successfully');
            setManualRyftAccountId('');
            fetchData();
            if (selectedApp?.id === appId) {
                const updated = await adminAPI.getApplication(appId);
                setSelectedApp(updated.data);
                setEditedApp(JSON.parse(JSON.stringify(updated.data)));
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to link RyftPay account');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRefreshRyftStatus = async (appId) => {
        setActionLoading(true);
        try {
            const response = await adminAPI.refreshRyftStatus(appId);
            toast.success(response.data.mock 
                ? `Status refreshed (MOCK): ${response.data.verification_status}` 
                : `Status refreshed: ${response.data.verification_status}`
            );
            fetchData();
            if (selectedApp?.id === appId) {
                const updated = await adminAPI.getApplication(appId);
                setSelectedApp(updated.data);
                setEditedApp(JSON.parse(JSON.stringify(updated.data)));
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to refresh RyftPay status');
        } finally {
            setActionLoading(false);
        }
    };

    const handleEnvironmentToggle = async (checked) => {
        const newEnv = checked ? 'production' : 'sandbox';
        try {
            await adminAPI.updateSettings({ ryft_environment: newEnv });
            setSettings(prev => ({ ...prev, ryft_environment: newEnv }));
            toast.success(`Switched to ${newEnv} environment`);
        } catch (err) {
            toast.error('Failed to update environment');
        }
    };

    const handleSaveApiKeys = async () => {
        setSavingKeys(true);
        try {
            const payload = {};
            if (sandboxKey && !sandboxKey.includes('•')) payload.ryft_sandbox_key = sandboxKey;
            if (productionKey && !productionKey.includes('•')) payload.ryft_production_key = productionKey;
            
            if (Object.keys(payload).length === 0) {
                toast.info('No changes to save');
                setSavingKeys(false);
                return;
            }
            await adminAPI.updateApiKeys(payload);
            toast.success('API keys updated successfully');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save API keys');
        } finally {
            setSavingKeys(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'approved': return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Approved</Badge>;
            case 'rejected': return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Rejected</Badge>;
            default: return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Pending</Badge>;
        }
    };

    const getRyftBadge = (status) => {
        if (!status) return <Badge variant="outline" className="text-muted-foreground">Not Started</Badge>;
        switch (status) {
            case 'verified': return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Verified</Badge>;
            case 'pending_verification': return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">Pending Verification</Badge>;
            case 'verification_required': return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Verification Required</Badge>;
            case 'verification_failed': return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Verification Failed</Badge>;
            case 'creation_failed': return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Creation Failed</Badge>;
            case 'link_generated': return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Link Sent</Badge>;
            case 'account_created': return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Account Created</Badge>;
            default: return <Badge variant="outline">{status}</Badge>;
        }
    };

    const isIndividual = (app) => ['Individual', 'Sole Trader'].includes(app?.organization?.entity_type);

    // Save redirect URL
    const handleSaveRedirectUrl = async () => {
        setSavingRedirectUrl(true);
        try {
            await adminAPI.updateSettings({ ryft_redirect_url: redirectUrl });
            toast.success('Redirect URL updated successfully');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save redirect URL');
        } finally {
            setSavingRedirectUrl(false);
        }
    };

    // Check if a field is locked (RyftPay account created and field was sent to RyftPay)
    const isFieldLocked = (section, field) => {
        if (!selectedApp?.ryft_account_id) return false;
        
        const lockedFields = {
            organization: ['organisation_name', 'entity_type', 'registration_number'],
            personal: ['first_name', 'surname', 'date_of_birth', 'gender', 'nationality', 
                      'address_1', 'address_2', 'town_city', 'county', 'eircode', 
                      'mobile_phone', 'mobile_country_code', 'email']
        };
        
        return lockedFields[section]?.includes(field) || false;
    };

    // Helper to render field with lock indicator
    const renderEditableField = (section, field, label, inputComponent, displayValue) => {
        const locked = isFieldLocked(section, field);
        return (
            <div className="space-y-2">
                <Label className="flex items-center gap-2">
                    {label}
                    {locked && <span className="text-xs text-yellow-500 flex items-center gap-1"><Key className="w-3 h-3" /> Locked</span>}
                </Label>
                {isEditing && !locked ? inputComponent : (
                    <p className={`text-sm p-2 bg-muted/30 rounded ${locked ? 'border border-yellow-500/30' : ''}`}>
                        {displayValue || 'N/A'}
                    </p>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b border-border bg-card">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <a href="https://geektickets.ie" className="flex items-center gap-2">
                            <img src={LOGO_URL} alt="GeekTickets" className="h-10" />
                        </a>
                        <Badge variant="outline" className="text-primary border-primary">Admin</Badge>
                    </div>
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={() => setSettingsDialogOpen(true)} data-testid="admin-settings-btn">
                            <Settings className="w-4 h-4 mr-2" />
                            Settings
                        </Button>
                        <span className="text-sm text-muted-foreground">{user?.email}</span>
                        <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
                            <LogOut className="w-4 h-4 mr-2" />
                            Logout
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Environment Banner */}
                {settings && (
                    <div className={`mb-6 p-3 rounded-lg flex items-center justify-between ${
                        settings.ryft_environment === 'production' 
                            ? 'bg-red-500/10 border border-red-500/30' 
                            : 'bg-yellow-500/10 border border-yellow-500/30'
                    }`}>
                        <div className="flex items-center gap-2">
                            <CreditCard className="w-4 h-4" />
                            <span className="text-sm font-medium">
                                RyftPay Environment: <span className="uppercase">{settings.ryft_environment}</span>
                            </span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                            {settings.ryft_environment === 'production' ? 'Live transactions enabled' : 'Test mode - no real charges'}
                        </span>
                    </div>
                )}

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <Card className="bg-card border-border">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <Users className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats?.total_applications || 0}</p>
                                    <p className="text-sm text-muted-foreground">Total Applications</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-card border-border">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                                    <Clock className="w-6 h-6 text-yellow-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats?.pending_applications || 0}</p>
                                    <p className="text-sm text-muted-foreground">Pending Review</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-card border-border">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                                    <CheckCircle className="w-6 h-6 text-green-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats?.approved_applications || 0}</p>
                                    <p className="text-sm text-muted-foreground">Approved</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-card border-border">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                    <CreditCard className="w-6 h-6 text-blue-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats?.ryft_verified || 0}</p>
                                    <p className="text-sm text-muted-foreground">RyftPay Verified</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Applications Table */}
                <Card className="bg-card border-border">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <div>
                            <CardTitle className="font-['Outfit']">Applications</CardTitle>
                            <CardDescription>Manage organiser onboarding applications</CardDescription>
                        </div>
                        <Button variant="outline" size="sm" onClick={fetchData}>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Refresh
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {applications.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>No applications yet</p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Organisation</TableHead>
                                        <TableHead>Contact</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>RyftPay</TableHead>
                                        <TableHead>Submitted</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {applications.map((app) => (
                                        <TableRow key={app.id} className="table-row-hover">
                                            <TableCell className="font-medium">{app.organization?.organisation_name || 'N/A'}</TableCell>
                                            <TableCell>
                                                <div>
                                                    <p className="text-sm">{app.personal?.first_name} {app.personal?.surname}</p>
                                                    <p className="text-xs text-muted-foreground">{app.personal?.email}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>{getStatusBadge(app.status)}</TableCell>
                                            <TableCell>{getRyftBadge(app.ryft_status)}</TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {new Date(app.created_at).toLocaleDateString('en-IE')}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button variant="ghost" size="sm" onClick={() => handleViewDetails(app)}>
                                                    View Details
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </main>

            {/* Application Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={(open) => { setDetailDialogOpen(open); if (!open) setIsEditing(false); }}>
                <DialogContent className="max-w-4xl max-h-[90vh]">
                    <DialogHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <DialogTitle className="font-['Outfit']">
                                    {selectedApp?.organization?.organisation_name}
                                </DialogTitle>
                                <DialogDescription>Application ID: {selectedApp?.id}</DialogDescription>
                            </div>
                            {!isEditing ? (
                                <Button variant="outline" size="sm" onClick={handleStartEditing}>
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Edit
                                </Button>
                            ) : (
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={handleCancelEditing}>
                                        <X className="w-4 h-4 mr-2" />
                                        Cancel
                                    </Button>
                                    <Button size="sm" onClick={handleSaveChanges} disabled={actionLoading}>
                                        {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        <Save className="w-4 h-4 mr-2" />
                                        Save
                                    </Button>
                                </div>
                            )}
                        </div>
                    </DialogHeader>

                    <ScrollArea className="max-h-[70vh] pr-4">
                        {selectedApp && editedApp && (
                            <>
                                {/* RyftPay Lock Warning */}
                                {selectedApp.ryft_account_id && isEditing && (
                                    <div className="mb-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-2">
                                        <Key className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm">
                                            <p className="font-medium text-yellow-500">Some fields are locked</p>
                                            <p className="text-muted-foreground">
                                                Fields sent to RyftPay cannot be edited after the account has been created. 
                                                Locked fields are marked with a <Key className="w-3 h-3 inline" /> icon.
                                            </p>
                                        </div>
                                    </div>
                                )}
                                
                                <Tabs defaultValue="organization" className="w-full">
                                <TabsList className="grid w-full grid-cols-4">
                                    <TabsTrigger value="organization">Organisation</TabsTrigger>
                                    <TabsTrigger value="personal">Personal</TabsTrigger>
                                    <TabsTrigger value="events">Events</TabsTrigger>
                                    <TabsTrigger value="ryftpay">RyftPay</TabsTrigger>
                                </TabsList>

                                {/* Organisation Tab */}
                                <TabsContent value="organization" className="space-y-4 pt-4">
                                    <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                                        <div>
                                            <Label className="text-muted-foreground">Application Status</Label>
                                            <div className="mt-1">{getStatusBadge(selectedApp.status)}</div>
                                        </div>
                                        <Select
                                            value={selectedApp.status}
                                            onValueChange={(value) => handleStatusChange(selectedApp.id, value)}
                                            disabled={actionLoading}
                                        >
                                            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="pending">Pending</SelectItem>
                                                <SelectItem value="approved">Approved</SelectItem>
                                                <SelectItem value="rejected">Rejected</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Organisation Name</Label>
                                            {isEditing ? (
                                                <Input value={editedApp.organization?.organisation_name || ''} onChange={(e) => updateEditedField('organization', 'organisation_name', e.target.value)} />
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.organization?.organisation_name}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Entity Type</Label>
                                            {isEditing ? (
                                                <Select value={editedApp.organization?.entity_type || ''} onValueChange={(value) => updateEditedField('organization', 'entity_type', value)}>
                                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        {ENTITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.organization?.entity_type}</p>
                                            )}
                                        </div>
                                        {!isIndividual(selectedApp) && (
                                            <>
                                                <div className="space-y-2">
                                                    <Label>CRO Number</Label>
                                                    {isEditing ? (
                                                        <Input value={editedApp.organization?.registration_number || ''} onChange={(e) => updateEditedField('organization', 'registration_number', e.target.value)} />
                                                    ) : (
                                                        <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.organization?.registration_number || 'N/A'}</p>
                                                    )}
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>VAT Number</Label>
                                                    {isEditing ? (
                                                        <Input value={editedApp.organization?.vat_number || ''} onChange={(e) => updateEditedField('organization', 'vat_number', e.target.value)} />
                                                    ) : (
                                                        <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.organization?.vat_number || 'N/A'}</p>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </TabsContent>

                                {/* Personal Tab */}
                                <TabsContent value="personal" className="space-y-4 pt-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>First Name</Label>
                                            {isEditing ? (
                                                <Input value={editedApp.personal?.first_name || ''} onChange={(e) => updateEditedField('personal', 'first_name', e.target.value)} />
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.personal?.first_name}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Surname</Label>
                                            {isEditing ? (
                                                <Input value={editedApp.personal?.surname || ''} onChange={(e) => updateEditedField('personal', 'surname', e.target.value)} />
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.personal?.surname}</p>
                                            )}
                                        </div>

                                        {isIndividual(selectedApp) && (
                                            <>
                                                <div className="space-y-2">
                                                    <Label>Date of Birth</Label>
                                                    {isEditing ? (
                                                        <Input value={editedApp.personal?.date_of_birth || ''} onChange={(e) => updateEditedField('personal', 'date_of_birth', e.target.value)} placeholder="DD/MM/YYYY or YYYY-MM-DD" />
                                                    ) : (
                                                        <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.personal?.date_of_birth || 'N/A'}</p>
                                                    )}
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Gender</Label>
                                                    {isEditing ? (
                                                        <Select value={editedApp.personal?.gender || ''} onValueChange={(value) => updateEditedField('personal', 'gender', value)}>
                                                            <SelectTrigger><SelectValue placeholder="Select gender" /></SelectTrigger>
                                                            <SelectContent>
                                                                {GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                                                            </SelectContent>
                                                        </Select>
                                                    ) : (
                                                        <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.personal?.gender || 'N/A'}</p>
                                                    )}
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Nationality</Label>
                                                    {isEditing ? (
                                                        <Select value={editedApp.personal?.nationality || ''} onValueChange={(value) => updateEditedField('personal', 'nationality', value)}>
                                                            <SelectTrigger><SelectValue placeholder="Select nationality" /></SelectTrigger>
                                                            <SelectContent>
                                                                {NATIONALITIES.map(n => <SelectItem key={n.code} value={n.code}>{n.name}</SelectItem>)}
                                                            </SelectContent>
                                                        </Select>
                                                    ) : (
                                                        <p className="text-sm p-2 bg-muted/30 rounded">{NATIONALITIES.find(n => n.code === selectedApp.personal?.nationality)?.name || selectedApp.personal?.nationality || 'N/A'}</p>
                                                    )}
                                                </div>
                                            </>
                                        )}

                                        <div className="space-y-2">
                                            <Label>Email</Label>
                                            {isEditing ? (
                                                <Input type="email" value={editedApp.personal?.email || ''} onChange={(e) => updateEditedField('personal', 'email', e.target.value)} />
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.personal?.email}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Mobile Phone</Label>
                                            {isEditing ? (
                                                <div className="flex gap-2">
                                                    <Input className="w-20" value={editedApp.personal?.mobile_country_code || '+353'} onChange={(e) => updateEditedField('personal', 'mobile_country_code', e.target.value)} />
                                                    <Input className="flex-1" value={editedApp.personal?.mobile_phone || ''} onChange={(e) => updateEditedField('personal', 'mobile_phone', e.target.value)} />
                                                </div>
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.personal?.mobile_country_code} {selectedApp.personal?.mobile_phone}</p>
                                            )}
                                        </div>

                                        <div className="space-y-2 col-span-2">
                                            <Label>Address Line 1</Label>
                                            {isEditing ? (
                                                <Input value={editedApp.personal?.address_1 || ''} onChange={(e) => updateEditedField('personal', 'address_1', e.target.value)} />
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.personal?.address_1}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2 col-span-2">
                                            <Label>Address Line 2</Label>
                                            {isEditing ? (
                                                <Input value={editedApp.personal?.address_2 || ''} onChange={(e) => updateEditedField('personal', 'address_2', e.target.value)} />
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.personal?.address_2 || 'N/A'}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Town/City</Label>
                                            {isEditing ? (
                                                <Input value={editedApp.personal?.town_city || ''} onChange={(e) => updateEditedField('personal', 'town_city', e.target.value)} />
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.personal?.town_city}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label>County</Label>
                                            {isEditing ? (
                                                <Select value={editedApp.personal?.county || ''} onValueChange={(value) => updateEditedField('personal', 'county', value)}>
                                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        {COUNTIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.personal?.county}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Eircode</Label>
                                            {isEditing ? (
                                                <Input value={editedApp.personal?.eircode || ''} onChange={(e) => updateEditedField('personal', 'eircode', e.target.value.toUpperCase())} />
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.personal?.eircode}</p>
                                            )}
                                        </div>
                                    </div>
                                </TabsContent>

                                {/* Events Tab */}
                                <TabsContent value="events" className="space-y-4 pt-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2 col-span-2">
                                            <Label>Event Types</Label>
                                            {isEditing ? (
                                                <Input value={editedApp.event_details?.event_types?.join(', ') || ''} onChange={(e) => updateEditedField('event_details', 'event_types', e.target.value.split(',').map(s => s.trim()))} placeholder="music, theatre, etc." />
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.event_details?.event_types?.join(', ') || 'N/A'}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Average Attendance</Label>
                                            {isEditing ? (
                                                <Input value={editedApp.event_details?.average_attendance || ''} onChange={(e) => updateEditedField('event_details', 'average_attendance', e.target.value)} />
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.event_details?.average_attendance}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Planned Events (Next Year)</Label>
                                            {isEditing ? (
                                                <Input value={editedApp.event_details?.planned_events || ''} onChange={(e) => updateEditedField('event_details', 'planned_events', e.target.value)} />
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.event_details?.planned_events}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2 col-span-2">
                                            <Label>How They Heard About Us</Label>
                                            {isEditing ? (
                                                <Textarea value={editedApp.event_details?.how_heard || ''} onChange={(e) => updateEditedField('event_details', 'how_heard', e.target.value)} />
                                            ) : (
                                                <p className="text-sm p-2 bg-muted/30 rounded">{selectedApp.event_details?.how_heard}</p>
                                            )}
                                        </div>
                                    </div>
                                </TabsContent>

                                {/* RyftPay Tab */}
                                <TabsContent value="ryftpay" className="space-y-4 pt-4">
                                    {/* Consent info moved here */}
                                    <div className="p-4 rounded-lg bg-muted/30 border border-border">
                                        <h4 className="font-medium mb-2">Consent</h4>
                                        <div className="space-y-2 text-sm">
                                            <p><span className="text-muted-foreground">Payment Authorization:</span> {selectedApp.consent?.payment_consent ? '✓ Accepted' : '✗ Not accepted'}</p>
                                            <p><span className="text-muted-foreground">Marketing Consent:</span> {selectedApp.consent?.marketing_consent ? '✓ Accepted' : '✗ Not accepted'}</p>
                                            {selectedApp.consent?.promo_code && <p><span className="text-muted-foreground">Promo Code:</span> {selectedApp.consent.promo_code}</p>}
                                        </div>
                                    </div>

                                    <div className="p-4 rounded-lg bg-muted/30 border border-border">
                                        <h4 className="font-medium mb-3 flex items-center gap-2">
                                            <CreditCard className="w-4 h-4" />
                                            RyftPay Integration
                                        </h4>
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <Label className="text-muted-foreground">Status</Label>
                                                    <div className="mt-1">{getRyftBadge(selectedApp.ryft_status)}</div>
                                                </div>
                                                {selectedApp.ryft_account_id && (
                                                    <div className="text-right">
                                                        <Label className="text-muted-foreground">Account ID</Label>
                                                        <p className="font-mono text-sm">{selectedApp.ryft_account_id}</p>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex gap-2 flex-wrap">
                                                {/* Show Create Account button when no account exists OR creation failed */}
                                                {(!selectedApp.ryft_account_id || selectedApp.ryft_status === 'creation_failed') && (
                                                    <Button 
                                                        size="sm" 
                                                        variant={selectedApp.ryft_status === 'creation_failed' ? 'destructive' : 'default'}
                                                        onClick={() => handleCreateRyftAccount(selectedApp.id)} 
                                                        disabled={actionLoading || selectedApp.status !== 'approved'}
                                                        data-testid="create-ryft-account-btn"
                                                    >
                                                        {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                                        {selectedApp.ryft_status === 'creation_failed' ? 'Retry Account Creation' : 'Create RyftPay Account'}
                                                    </Button>
                                                )}
                                                {/* Show Generate Link button when account exists but no link */}
                                                {selectedApp.ryft_account_id && !selectedApp.ryft_onboarding_url && selectedApp.ryft_status !== 'creation_failed' && (
                                                    <Button size="sm" onClick={() => handleCreateRyftLink(selectedApp.id)} disabled={actionLoading}>
                                                        {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                                        Generate Onboarding Link
                                                    </Button>
                                                )}
                                                {/* Show Copy/Open buttons when link exists */}
                                                {selectedApp.ryft_onboarding_url && (
                                                    <>
                                                        <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(selectedApp.ryft_onboarding_url); toast.success('Link copied!'); }}>
                                                            <Copy className="w-4 h-4 mr-2" />
                                                            Copy Link
                                                        </Button>
                                                        <Button size="sm" onClick={() => window.open(selectedApp.ryft_onboarding_url, '_blank')}>
                                                            <ExternalLink className="w-4 h-4 mr-2" />
                                                            Open
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                            
                                            {/* Help text for failed or missing account */}
                                            {selectedApp.ryft_status === 'creation_failed' && (
                                                <p className="text-sm text-red-400 mt-2">
                                                    Account creation failed. Click "Retry Account Creation" to try again.
                                                </p>
                                            )}
                                            {!selectedApp.ryft_account_id && selectedApp.status !== 'approved' && (
                                                <p className="text-sm text-muted-foreground mt-2">
                                                    Approve the application first to enable RyftPay account creation.
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Manual Link Account - shown when no RyftPay account */}
                                    {!selectedApp.ryft_account_id && (
                                        <div className="p-4 rounded-lg bg-muted/30 border border-border">
                                            <h4 className="font-medium mb-3 flex items-center gap-2">
                                                <Link2 className="w-4 h-4" />
                                                Link Existing RyftPay Account
                                            </h4>
                                            <p className="text-sm text-muted-foreground mb-3">
                                                If an account was created directly in RyftPay, enter the Account ID to link it.
                                            </p>
                                            <div className="flex gap-2">
                                                <Input 
                                                    placeholder="ac_xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                                    value={manualRyftAccountId}
                                                    onChange={(e) => setManualRyftAccountId(e.target.value)}
                                                    className="font-mono text-sm flex-1"
                                                />
                                                <Button size="sm" onClick={() => handleLinkRyftAccount(selectedApp.id)} disabled={actionLoading || !manualRyftAccountId.trim()}>
                                                    {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                                    Link
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Verification Status Details */}
                                    {selectedApp.ryft_account_id && (
                                        <div className="p-4 rounded-lg bg-muted/30 border border-border">
                                            <h4 className="font-medium mb-3 flex items-center gap-2">
                                                <CheckCircle className="w-4 h-4" />
                                                Verification Status
                                            </h4>
                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <Label className="text-muted-foreground">Account Verification</Label>
                                                    <div className="mt-1">{getRyftBadge(selectedApp.ryft_status)}</div>
                                                </div>
                                                {selectedApp.ryft_verification_status && (
                                                    <div>
                                                        <Label className="text-muted-foreground">RyftPay Status</Label>
                                                        <p className="mt-1 text-sm">{selectedApp.ryft_verification_status}</p>
                                                    </div>
                                                )}
                                                {selectedApp.ryft_person_verification_status && (
                                                    <div>
                                                        <Label className="text-muted-foreground">Person Verification</Label>
                                                        <p className="mt-1 text-sm">{selectedApp.ryft_person_verification_status}</p>
                                                    </div>
                                                )}
                                                {selectedApp.ryft_last_webhook_at && (
                                                    <div>
                                                        <Label className="text-muted-foreground">Last Webhook Update</Label>
                                                        <p className="mt-1 text-sm">{new Date(selectedApp.ryft_last_webhook_at).toLocaleString('en-IE')}</p>
                                                    </div>
                                                )}
                                            </div>
                                            <Button 
                                                size="sm" 
                                                variant="outline" 
                                                onClick={() => handleRefreshRyftStatus(selectedApp.id)} 
                                                disabled={actionLoading}
                                                className="mt-3"
                                            >
                                                {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                                                Refresh Status from RyftPay
                                            </Button>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <Label className="text-muted-foreground">Created</Label>
                                            <p>{new Date(selectedApp.created_at).toLocaleString('en-IE')}</p>
                                        </div>
                                        <div>
                                            <Label className="text-muted-foreground">Last Updated</Label>
                                            <p>{new Date(selectedApp.updated_at).toLocaleString('en-IE')}</p>
                                        </div>
                                    </div>
                                </TabsContent>
                            </Tabs>
                            </>
                        )}
                    </ScrollArea>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Settings Dialog */}
            <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-['Outfit']">Admin Settings</DialogTitle>
                        <DialogDescription>Configure RyftPay integration settings</DialogDescription>
                    </DialogHeader>

                    {settings && (
                        <Tabs defaultValue="environment" className="w-full">
                            <TabsList className="grid w-full grid-cols-3">
                                <TabsTrigger value="environment">Environment</TabsTrigger>
                                <TabsTrigger value="redirect">Redirect URL</TabsTrigger>
                                <TabsTrigger value="apikeys">API Keys</TabsTrigger>
                            </TabsList>
                            
                            <TabsContent value="environment" className="space-y-4 pt-4">
                                <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                                    <div>
                                        <Label>RyftPay Environment</Label>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            {settings.ryft_environment === 'production' ? 'Live mode - real transactions' : 'Sandbox mode - test transactions'}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-muted-foreground">Sandbox</span>
                                        <Switch checked={settings.ryft_environment === 'production'} onCheckedChange={handleEnvironmentToggle} />
                                        <span className="text-sm text-muted-foreground">Production</span>
                                    </div>
                                </div>
                            </TabsContent>
                            
                            <TabsContent value="redirect" className="space-y-4 pt-4">
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            <ExternalLink className="w-4 h-4" />
                                            Onboarding Completion Redirect URL
                                        </Label>
                                        <Input 
                                            type="url" 
                                            value={redirectUrl} 
                                            onChange={(e) => setRedirectUrl(e.target.value)} 
                                            placeholder="https://geektickets.ie/onboarding-complete"
                                            className="font-mono text-sm"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Users will be redirected to this URL after completing RyftPay onboarding
                                        </p>
                                    </div>
                                    
                                    <Button onClick={handleSaveRedirectUrl} disabled={savingRedirectUrl} className="w-full">
                                        {savingRedirectUrl && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        <Save className="w-4 h-4 mr-2" />
                                        Save Redirect URL
                                    </Button>
                                </div>
                            </TabsContent>
                            
                            <TabsContent value="apikeys" className="space-y-4 pt-4">
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2"><Key className="w-4 h-4" />Sandbox API Key</Label>
                                        <div className="relative">
                                            <Input type={showSandboxKey ? "text" : "password"} value={sandboxKey} onChange={(e) => setSandboxKey(e.target.value)} placeholder="sk_sandbox_..." className="pr-10 font-mono text-sm" />
                                            <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3" onClick={() => setShowSandboxKey(!showSandboxKey)}>
                                                {showSandboxKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </Button>
                                        </div>
                                        <p className="text-xs text-muted-foreground">{settings.ryft_sandbox_key_set ? '✓ Sandbox key is configured' : '⚠ No sandbox key configured'}</p>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2"><Key className="w-4 h-4" />Production API Key</Label>
                                        <div className="relative">
                                            <Input type={showProductionKey ? "text" : "password"} value={productionKey} onChange={(e) => setProductionKey(e.target.value)} placeholder="sk_live_..." className="pr-10 font-mono text-sm" />
                                            <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3" onClick={() => setShowProductionKey(!showProductionKey)}>
                                                {showProductionKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </Button>
                                        </div>
                                        <p className="text-xs text-muted-foreground">{settings.ryft_production_key_set ? '✓ Production key is configured' : '⚠ No production key configured'}</p>
                                    </div>

                                    <Button onClick={handleSaveApiKeys} disabled={savingKeys} className="w-full">
                                        {savingKeys && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        <Save className="w-4 h-4 mr-2" />
                                        Save API Keys
                                    </Button>
                                </div>
                            </TabsContent>
                        </Tabs>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
